import React, { forwardRef, useCallback, useState } from 'react';
import { FieldTypes } from "utils/formUtils";
import { Alert, Form, Tooltip } from "antd";
import { MODAL_SIZE_MEDIUM } from 'constants/Layout';
import message from 'components/CustomMessage';
import csvtojson from 'csvtojson';
import { checkForAdditionalColumns, validateUpdateFindingCSVFile } from './utils';
import _ from 'lodash';
import { HelpSolidIcon } from 'components/Icons';
import BaseModalForm from 'components/BaseModalForm';
import FindingAPI from 'restAPI/FindingAPI';
import ContentOverlay from 'components/ContentOverlay';
import ModalUtil from 'utils/modalUtil';
import ConfirmPopup from 'components/ConfirmPopup';
import useEnabledFindingStatus from 'hooks/useEnabledFindingStatus';
import Vocabulary from 'components/Vocabulary';
/**
 * CSV_ACCEPT_MIME_TYPES : possible mime types for csv files
 * csv on windows can have a different mime type depending upon the installed/default MS-EXCEL version
 * on the machine.
 */
export const CSV_ACCEPT_MIME_TYPES = [
	'text/csv',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

const SUPPORTED_COLUMNS = ["Finding ID", "Status", "Severity", "Category", "Comment", "Resurface Date"];

const UpdateFindingFromCSV = forwardRef((props, ref) => {
	const [data, setData] = useState({});
	const [form] = Form.useForm();
	const [fileList, setFileList] = useState([]);
	const [additionalColumns, setAdditionalColumns] = useState([]);
	const [success, setSuccess] = useState(false);
	const [CSVFileError, setCSVFileError] = useState();
	const [disableSubmit, setDisableSubmit] = useState(true);
	const [loading, setLoading] = useState(false);
	const enabledFindingStatus = useEnabledFindingStatus();

	const validateFile = (file) => {
		if (!CSV_ACCEPT_MIME_TYPES.includes(file.type)) {
			message.error("Please upload csv file only");
			return Promise.reject();
		}
		const reader = new FileReader();
		reader.readAsText(file);
		reader.onload = async function () {
			const jsonData = await csvtojson().fromString(reader.result);
			setAdditionalColumns(checkForAdditionalColumns(jsonData, SUPPORTED_COLUMNS));
			const errorList = validateUpdateFindingCSVFile(jsonData, enabledFindingStatus);
			if (!_.isEmpty(errorList)) {
				setDisableSubmit(true)
				setCSVFileError(errorList);
				setFileList([])
				form.resetFields(["csvFile"])
			} else {
				setDisableSubmit(false)
				setFileList([file]);
			}
		};

		return false;
	}

	const upload = useCallback(async () => {
		setLoading(true)
		try {
			let formData = new FormData();
			formData.append('file', fileList[0])
			const res = await FindingAPI.uploadFindingCSV(formData).promise;
			setLoading(false);
			setSuccess(true);
			message.success(res.message);
		}
		catch (e) {
			setLoading(false)
		}
	}, [fileList]);

	const submitForm = async () => {
		await form.validateFields();
		if (!_.isEmpty(additionalColumns)) {
            ModalUtil.show({
                content: (
                    <ConfirmPopup 
                        promptMessage={<>Only Status, <Vocabulary name="severity"/>, Category, Tags, <Vocabulary name="resolution" /> Due Date and Comments will be updated</>}
                        okText={"Yes"}
                        onOk={upload}
                        modalProps={{
                            cancelBtnText: "Cancel"
                        }}
                    />
                )
            })
		} else {
			upload();
		}
	}

	const fileChange = (file) => {
		setCSVFileError();
		if (file.file.status === 'removed') {
			setFileList([])
			form.resetFields(["csvFile"])
		}
	}
	const importFindingInfo = "Finding ID is a required field in the CSV to update findings.";
	const fields = [{
		value: <span className='pull-right'><a href='/downloads/updateFindingTemplate.csv' download>Download Sample CSV</a> <Tooltip title={importFindingInfo}><HelpSolidIcon /></Tooltip></span>,
		type: FieldTypes.LABEL,
		noStyle: true,
	}, {
		name: "csvFile",
		label: "Attach CSV file",
		type: FieldTypes.ATTACHMENT,
		required: true,
		uploadProps: {
			beforeUpload: validateFile,
			accept: ".csv",
			onChange: fileChange,
			fileList
		},
		rules: [{
			validator: (_rule, files = {}) => {
				const { file } = files
				if (file) {
					if ((file.size) / 1000000 > 50) {
						return Promise.reject("All file size should be less than 50 MB");
					}
				}
				return Promise.resolve();
			}
		}]
	}]

	return (
		<BaseModalForm
			ref={ref}
			form={form}
			loading={loading}
			afterClose={() => props.afterClose(success)}
			onValuesChange={(_, values) => setData({ ...data, ...values })}
			okButtonProps={{ disabled: disableSubmit }}
			title={"Update Findings via CSV File"}
			onOk={submitForm}
			fields={fields}
			width={MODAL_SIZE_MEDIUM}
		>
			{!_.isEmpty(CSVFileError) && <Alert
				closable
				onClose={() => setCSVFileError()}
				description={
					<ContentOverlay className='markdown-text'
						content={
							<ul>
								{CSVFileError.map(err => <li key={err}>{err}</li>)}
							</ul>
						}
					/>
				}
				type="error" />}
		</BaseModalForm>
	)
})

export default UpdateFindingFromCSV;
